/**
 * Snapshot refresh state for the Forecast tab.
 *
 * Only meaningful on installs with the pre-aggregated snapshot (the
 * po_line_schedules tenants, the scheduling tenant). Elsewhere the status endpoint answers
 * `{ enabled: false }` and the Refresh button stays hidden; a refresh request
 * answers `{ skipped: true }`.
 */
import { useCallback, useEffect, useState } from 'react';
import { purchasingAPI } from '../../../../lib/api';
import { toast } from 'sonner';

export interface ForecastRefreshStatus {
  enabled: boolean;
  last_refreshed_at: string | null;
  last_status?: string;
  last_row_count?: number;
}

/**
 * Loads the snapshot's refresh status and triggers / polls a refresh.
 *
 * @param onRefreshed - reloads the grid once a refresh has completed
 * @returns `refreshStatus`, `refreshing` and `handleRefreshSnapshot`
 * @example
 * const { refreshStatus, refreshing, handleRefreshSnapshot } = useForecastSnapshotRefresh(fetchData);
 */
export function useForecastSnapshotRefresh(onRefreshed: () => Promise<void>) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<ForecastRefreshStatus | null>(null);

  useEffect(() => {
    purchasingAPI.getForecastRefreshStatus()
      .then((resp) => setRefreshStatus(resp.data))
      .catch(() => setRefreshStatus(null));
  }, []);

  const handleRefreshSnapshot = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const resp = await purchasingAPI.refreshForecastSnapshot();
      if (resp.data?.skipped) {
        toast.info('Snapshot refresh is not enabled for this install.');
        setRefreshing(false);
        return;
      }
      toast.success('Refresh queued — fetching new data when ready.');
      // Poll the refresh-status endpoint until last_status flips to 'ok'.
      const startedAt = new Date(refreshStatus?.last_refreshed_at || 0).getTime();
      let attempts = 0;
      const poll = async (): Promise<void> => {
        attempts++;
        if (attempts > 60) {  // hard cap ≈ 2 minutes at 2s intervals
          setRefreshing(false);
          toast.error('Refresh is taking longer than expected — try again later.');
          return;
        }
        const statusResp = await purchasingAPI.getForecastRefreshStatus();
        const completedAt = new Date(statusResp.data?.last_refreshed_at || 0).getTime();
        if (statusResp.data?.last_status === 'ok' && completedAt > startedAt) {
          setRefreshStatus(statusResp.data);
          await onRefreshed();
          setRefreshing(false);
          toast.success('Forecast snapshot refreshed.');
          return;
        }
        if (statusResp.data?.last_status === 'failed') {
          setRefreshStatus(statusResp.data);
          setRefreshing(false);
          toast.error('Refresh failed — check server logs.');
          return;
        }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (err: unknown) {
      console.error('Forecast snapshot refresh failed:', err);
      toast.error('Failed to trigger refresh.');
      setRefreshing(false);
    }
  }, [refreshing, refreshStatus, onRefreshed]);

  return { refreshStatus, refreshing, handleRefreshSnapshot };
}
